import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { Bold, Code, Italic, Link2, List, ListOrdered, Strikethrough, Unlink } from 'lucide-react';
import { Button, Input, Popover, PopoverContent, PopoverTrigger, Tooltip, cn } from '../ui/index.js';

/**
 * The reply and note composer: rich text on screen, Markdown underneath.
 *
 * What it saves is the small Markdown `@esa/shared` renders (markdown.ts):
 * bold, italic, strike, inline code, links and lists. Everything else the
 * editor could do is switched off, so nothing typed or pasted here can be
 * something the email and the ticket screen cannot show. Pasting from
 * Outlook or a web page keeps that much formatting and drops the rest.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  disabled,
  onSubmit,
}: {
  /** Markdown. */
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  /** Ctrl/Cmd+Enter. */
  onSubmit?: () => void;
}) {
  // The last Markdown this editor produced, so a value coming back in from
  // the parent is not re-applied (which would move the caret).
  const emitted = useRef(value);
  const submit = useRef(onSubmit);
  submit.current = onSubmit;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        underline: false,
        link: {
          openOnClick: false,
          autolink: true,
          protocols: ['mailto'],
          HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
          isAllowedUri: (url) => /^(https?:\/\/|mailto:)/i.test(url),
        },
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      Markdown.configure({ html: false, breaks: true, linkify: true, transformPastedText: true, tightLists: true }),
    ],
    content: value,
    editable: !disabled,
    immediatelyRender: true,
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        'aria-multiline': 'true',
        role: 'textbox',
        class: 'rich-text min-h-[6rem] px-2.5 py-2 text-sm leading-relaxed text-ink focus:outline-none',
      },
      handleKeyDown: (_view, e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && submit.current) {
          submit.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const md = markdownOf(ed);
      emitted.current = md;
      onChange(md);
    },
  });

  // A value set from outside (cleared after sending, an AI Proof suggestion
  // used, a suggested reply inserted) replaces the content.
  useEffect(() => {
    if (!editor || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  return (
    <div
      className={cn(
        'rounded-md bg-surface ring-1 ring-inset ring-line-strong/70 transition-[box-shadow] duration-fast',
        'focus-within:ring-2 focus-within:ring-brand',
        disabled && 'bg-surface-sunken',
        className,
      )}
    >
      {editor && <Toolbar editor={editor} disabled={disabled} />}
      <EditorContent editor={editor} />
    </div>
  );
}

function markdownOf(editor: Editor): string {
  const storage = editor.storage as unknown as { markdown?: { getMarkdown(): string } };
  return (storage.markdown?.getMarkdown() ?? editor.getText()).trim();
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled?: boolean }) {
  // Re-render on selection and content changes so the pressed states follow the caret.
  const [, tick] = useState(0);
  useEffect(() => {
    const on = () => tick((n) => n + 1);
    editor.on('transaction', on);
    return () => {
      editor.off('transaction', on);
    };
  }, [editor]);

  const chain = () => editor.chain().focus();
  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex flex-wrap items-center gap-0.5 border-b border-line-subtle px-1.5 py-1"
    >
      <Tool label="Bold (Ctrl+B)" active={editor.isActive('bold')} disabled={disabled} onClick={() => chain().toggleBold().run()}>
        <Bold />
      </Tool>
      <Tool label="Italic (Ctrl+I)" active={editor.isActive('italic')} disabled={disabled} onClick={() => chain().toggleItalic().run()}>
        <Italic />
      </Tool>
      <Tool label="Strikethrough" active={editor.isActive('strike')} disabled={disabled} onClick={() => chain().toggleStrike().run()}>
        <Strikethrough />
      </Tool>
      <Tool label="Code" active={editor.isActive('code')} disabled={disabled} onClick={() => chain().toggleCode().run()}>
        <Code />
      </Tool>
      <span aria-hidden className="mx-1 h-4 w-px bg-line" />
      <Tool label="Bulleted list" active={editor.isActive('bulletList')} disabled={disabled} onClick={() => chain().toggleBulletList().run()}>
        <List />
      </Tool>
      <Tool label="Numbered list" active={editor.isActive('orderedList')} disabled={disabled} onClick={() => chain().toggleOrderedList().run()}>
        <ListOrdered />
      </Tool>
      <span aria-hidden className="mx-1 h-4 w-px bg-line" />
      <LinkTool editor={editor} disabled={disabled} />
    </div>
  );
}

function Tool({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip content={label}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={label}
        aria-pressed={active}
        disabled={disabled}
        // Keep the editor's selection: a mousedown on the button would blur it.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        className={cn('[&_svg]:h-3.5 [&_svg]:w-3.5', active && 'bg-surface-active text-ink')}
      >
        {children}
      </Button>
    </Tooltip>
  );
}

function LinkTool({ editor, disabled }: { editor: Editor; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState('');
  const active = editor.isActive('link');
  const normal = (u: string) => {
    const t = u.trim();
    if (!t) return '';
    if (/^(https?:\/\/|mailto:)/i.test(t)) return t;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return `mailto:${t}`;
    return `https://${t}`;
  };
  const apply = () => {
    const url = normal(href);
    const chain = editor.chain().focus().extendMarkRange('link');
    if (!url) chain.unsetLink().run();
    else if (editor.state.selection.empty && !active) {
      // Nothing selected: insert the address as its own link text.
      editor.chain().focus().insertContent({ type: 'text', text: href.trim(), marks: [{ type: 'link', attrs: { href: url } }] }).run();
    } else chain.setLink({ href: url }).run();
    setOpen(false);
  };
  return (
    <>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (o) setHref((editor.getAttributes('link').href as string | undefined) ?? '');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Link"
            aria-pressed={active}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            className={cn('[&_svg]:h-3.5 [&_svg]:w-3.5', active && 'bg-surface-active text-ink')}
          >
            <Link2 />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2">
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
          >
            <Input
              size="sm"
              value={href}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHref(e.target.value)}
              placeholder="https://… or someone@esassoc.com"
              aria-label="Link address"
              autoFocus
            />
            <Button size="sm" type="submit">
              {active ? 'Update' : 'Add'}
            </Button>
          </form>
        </PopoverContent>
      </Popover>
      {active && (
        <Tool label="Remove link" disabled={disabled} onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}>
          <Unlink />
        </Tool>
      )}
    </>
  );
}
